/** Governance rule category (a closed set). */
export enum RuleType {
	BRAND = 'Brand',
	CHANNEL = 'Channel',
	REGIONAL = 'Regional',
	COMPLIANCE = 'Compliance',
}

/** Rule severity — drives precedence: blocking > warning. Advisory only on
 *  model-generated content (ADR-0019). */
export enum RuleSeverity {
	BLOCKING = 'blocking',
	WARNING = 'warning',
}

/**
 * The kinds of element a rule can govern (ADR-0018). Deliberately code-defined
 * rather than axis-registry data: an element type names something ATLAS's own code
 * acts on, so adding one always implies code that handles it — a value nobody can
 * act on would be meaningless.
 *
 * Orthogonal to `RuleType` (what kind of rule) and `RuleValidationMethod` (how it
 * is checked): a model-judged rule can still govern a video's copy.
 */
export enum RuleElementType {
	TEXT = 'text',
	FILE = 'file',
	IMAGE = 'image',
	VIDEO = 'video',
	WEBSITE = 'website',
	CONTACT = 'contact',
}

/**
 * How a validation run answers a rule - the only distinction that changes the cost
 * and the confidence of a verdict: whether a model is called at all.
 *
 * `METADATA` is checked deterministically against the subject's metadata document
 * via the rule's `metadataCheck`, with no provider request. `MULTIMODAL_LLM` is
 * judged by a model in one request carrying the subject's text, metadata and image
 * bytes together: there is no separate text-only or image-only path, because which
 * content blocks a request assembles is plumbing, not a rule property (ADR-0019).
 */
export enum RuleValidationMethod {
	METADATA = 'metadata',
	MULTIMODAL_LLM = 'multimodal-LLM',
}

/** Rule lifecycle: draft → active → retired. */
export enum RuleLifecycle {
	DRAFT = 'draft',
	ACTIVE = 'active',
	RETIRED = 'retired',
}

/**
 * The whole vocabulary of a deterministic metadata check. `PRESENT` means the path
 * resolves to a value that is not null, not an empty string and not an empty array;
 * `ABSENT` is its exact negation. An empty array reading as absent is load-bearing:
 * a classification axis is a key on every classified object whether or not it
 * carries a value, so a truthiness test would vacuously pass a rule demanding the
 * attribute be set.
 */
export enum RuleMetadataOperator {
	PRESENT = 'present',
	ABSENT = 'absent',
}
